import { Badge, Button, Card, Flex, Link, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, ListChecks, PlayCircle } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";
import { useTRPC } from "../../../../trpc/client";
import { useMerchant } from "../../../../hooks/useMerchant";
import { usePermissions } from "../../../../hooks/usePermissions";
import { ONBOARDING_LINKS } from "../../../public/onboarding/constants";

type ItemKey = "erpConfig" | "pseCode";

const OnboardingChecklist = (): JSX.Element => {
  const trpc = useTRPC();
  const { merchant } = useMerchant();
  const { isMerchantAdmin } = usePermissions();
  const [hidden, setHidden] = useLocalStorage<ItemKey[]>(
    `palomma:checklist-hidden:${merchant?.id ?? ""}`,
    []
  );
  const { data: catalogs } = useQuery(trpc.onboarding.catalogs.queryOptions());
  const { data: pseServiceCode } = useQuery({
    ...trpc.merchants.gatewayServiceCode.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
    }),
    enabled: !!merchant?.gateway,
  });

  if (!merchant || !isMerchantAdmin) return <></>;

  const erpName =
    catalogs?.erps.find((erp) => erp.value === merchant.erp)?.label ?? "tu ERP";

  const contactLink = (
    <Link
      href={ONBOARDING_LINKS.whatsappSupport}
      target="_blank"
      rel="noreferrer"
      color="palomma.700"
      textDecoration="underline"
      textUnderlineOffset="4px"
    >
      escríbenos
    </Link>
  );

  const items: { key: ItemKey; title: string; description: React.ReactNode }[] =
    [
      ...(merchant.erp !== "none" && !merchant.erpConfigured
        ? [
            {
              key: "erpConfig" as const,
              title: "Configuración del ERP pendiente",
              description: (
                <>
                  Para sincronizar la facturación faltan las credenciales (token
                  de acceso). Gestiónalas con <b>{erpName}</b>.
                </>
              ),
            },
          ]
        : []),
      ...(merchant.gateway
        ? [
            {
              key: "pseCode" as const,
              title: "Revisa el código de servicio PSE",
              description: pseServiceCode ? (
                <>
                  Tu código de servicio PSE es <b>{pseServiceCode}</b>. Verifica
                  que sea el correcto; si no, {contactLink}.
                </>
              ) : (
                <>
                  Verifica que tu código de servicio PSE sea el correcto; si no,{" "}
                  {contactLink}.
                </>
              ),
            },
          ]
        : []),
    ].filter((item) => !hidden.includes(item.key));

  if (!items.length) return <></>;

  return (
    <Card.Root
      data-tour="checklist"
      bg="palomma.50"
      rounded="md"
      borderWidth="1px"
      borderColor="palomma.200"
      borderLeftWidth="4px"
      borderLeftColor="palomma.500"
      boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
      px={3}
      py={2}
    >
      <Card.Body p={0} display="flex" flexDirection="column" gap={1.5}>
        <Flex align="center" gap={1.5}>
          <ListChecks size={16} color="var(--chakra-colors-palomma-600)" />
          <Text fontWeight="semibold" fontSize="sm" color="palomma.800">
            Últimos pasos de configuración
          </Text>
          <Badge colorPalette="purple" rounded="full" px={1.5} size="sm">
            {items.length} pendiente{items.length > 1 ? "s" : ""}
          </Badge>
        </Flex>

        {items.map((item) => (
          <Flex
            key={item.key}
            gap={2}
            align="center"
            bg="white"
            borderWidth="1px"
            borderColor="palomma.100"
            rounded="md"
            px={2.5}
            py={1.5}
          >
            <CircleAlert
              size={14}
              color="var(--chakra-colors-palomma-600)"
            />
            <Text flex="1" fontSize="sm" lineHeight="short" minW={0}>
              <Text as="span" fontWeight="semibold">
                {item.title}.{" "}
              </Text>
              <Text as="span" color="gray.500">
                {item.description}
              </Text>
              {item.key === "pseCode" && (
                <Link
                  href={ONBOARDING_LINKS.gatewayVideo}
                  target="_blank"
                  rel="noreferrer"
                  color="purple.600"
                  ml={1}
                  whiteSpace="nowrap"
                >
                  <PlayCircle
                    size={12}
                    style={{ display: "inline", verticalAlign: "-1px" }}
                  />{" "}
                  Ver tutorial
                </Link>
              )}
            </Text>
            <Button
              size="xs"
              colorPalette="purple"
              flexShrink={0}
              onClick={() => setHidden([...hidden, item.key])}
            >
              Listo
            </Button>
          </Flex>
        ))}
      </Card.Body>
    </Card.Root>
  );
};

export default OnboardingChecklist;
